import React, { useState, useEffect } from "react";
import LeftSection from "./components/LeftSection";
import RightSection from "./components/RightSection";
import {
  calculateKeywordDensity,
  calculateSpecificKeywordDensity,
} from "./utils/calculateDensity";
import { HelpModal } from "../common/compoents/HelpModal";
import { IoMdPlay } from "react-icons/io";
import { getKeywordsFromAI } from "../../services/getAiResponse";
import { useKeywordStore } from "./stores/keywordStore";
import { TableData } from "./types";
import { ToolsHeader } from "../components/ToolsHeader";
import { isInIframe } from "../../utils/is-in-iframe";
import { Box } from "./components/KeywordDensityStyle";

const KeywordDensityChecker: React.FC = () => {
  const [text, setText] = useState("");
  const [url, setUrl] = useState("");
  const [keyword, setKeyword] = useState("");
  const [filter, setFilter] = useState<"single" | "short" | "long">("single");
  const [keywordData, setKeywordData] = useState<
    { keyword: string; count: number; density: string }[]
  >([]);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [primaryKeyword, setPrimaryKeyword] = useState("");
  const [secondaryKeyword1, setSecondaryKeyword1] = useState("");
  const [secondaryKeyword2, setSecondaryKeyword2] = useState("");
  const [primaryKeywordData, setPrimaryKeywordData] = useState({
    count: 0,
    density: "0%",
  });
  const [secondaryKeyword1Data, setSecondaryKeyword1Data] = useState({
    count: 0,
    density: "0%",
  });
  const [secondaryKeyword2Data, setSecondaryKeyword2Data] = useState({
    count: 0,
    density: "0%",
  });
  const [keywordPlacementData, setKeywordPlacementData] = useState<TableData[]>(
    []
  );
  const [hasFetched, setHasFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [activeTab, setActiveTab] = useState<"text" | "url">("text");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { getKeywords, setKeywords } = useKeywordStore();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const openVideoModal = () => setShowVideoModal(true);
  const closeVideoModal = () => setShowVideoModal(false);

  const handleUrlChange = (newUrl: string) => setUrl(newUrl);
  const handleKeywordChange = (newKeyword: string) => setKeyword(newKeyword);
  const handleTableDataChange = (data: TableData[]) => {
    setKeywordPlacementData(data);
    setHasFetched(true);
  };
  const handleActiveTabChange = (tab: "text" | "url") => {
    setActiveTab(tab);
    setErrorMessage(null);
  };

  const handleSubmit = (calculatePlacement?: boolean) => {
    setPrimaryKeywordData(
      primaryKeyword
        ? calculateSpecificKeywordDensity(text, primaryKeyword)
        : { count: 0, density: "0%" }
    );
    setSecondaryKeyword1Data(
      secondaryKeyword1
        ? calculateSpecificKeywordDensity(text, secondaryKeyword1)
        : { count: 0, density: "0%" }
    );
    setSecondaryKeyword2Data(
      secondaryKeyword2
        ? calculateSpecificKeywordDensity(text, secondaryKeyword2)
        : { count: 0, density: "0%" }
    );
  };

  const helpSteps = [
    "Copy and paste the text you want to analyze into the provided input area.",
    "Click 'Short-Tail (2 Words)' or 'Long-Tail (3 Words)' to extract relevant keywords using AI, or use 'Single Words' for default analysis.",
    "Review the AI-generated keyword analysis to optimize your content for SEO.",
  ];

  useEffect(() => {
    const updateKeywordData = async () => {
      if (!text) {
        setKeywordData([]);
        setLoading(false);
        setErrorMessage(null);
        return;
      }

      setLoading(true);
      setErrorMessage(null);

      try {
        console.log(`Processing filter: ${filter}`);
        if (filter === "single") {
          const data = calculateKeywordDensity(text, filter);
          console.log("Single keywords:", data);
          setKeywordData(data);
        } else {
          const cachedKeywords = getKeywords(text, filter);
          if (cachedKeywords) {
            console.log(`Using cached ${filter} keywords:`, cachedKeywords);
            const data = cachedKeywords.map((keyword: string) => {
              const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
              const count = (
                text.match(new RegExp(`\\b${escapedKeyword}\\b`, "gi")) || []
              ).length;
              const totalWords = text.split(/\s+/).length;
              const density =
                totalWords > 0
                  ? `${Math.round((count / totalWords) * 100)}%`
                  : "0%";
              return { keyword, count, density };
            });
            setKeywordData(data);
          } else {
            console.log(`Fetching ${filter} keywords from AI...`);
            const aiKeywords = await getKeywordsFromAI(text, filter);
            console.log(`AI ${filter} keywords response:`, aiKeywords);
            if (Array.isArray(aiKeywords) && aiKeywords.length > 0) {
              setKeywords(text, filter, aiKeywords);
              const data = aiKeywords.map((keyword: string) => {
                const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
                const count = (
                  text.match(new RegExp(`\\b${escapedKeyword}\\b`, "gi")) || []
                ).length;
                const totalWords = text.split(/\s+/).length;
                const density =
                  totalWords > 0
                    ? `${Math.round((count / totalWords) * 100)}%`
                    : "0%";
                return { keyword, count, density };
              });
              setKeywordData(data);
            } else {
              console.warn(`No ${filter} keywords returned from AI`);
              setKeywordData(calculateKeywordDensity(text, filter));
              // Only set error for actual API errors, not empty responses
            }
          }
        }
      } catch (error: any) {
        console.error(`Error fetching ${filter} keywords:`, error.response?.data || error.message);
        setKeywordData(calculateKeywordDensity(text, filter));
        setErrorMessage(
          error.response?.data?.errors === "jwt malformed"
            ? "Authentication error: Invalid JWT token. Using local keyword analysis."
            : "Failed to fetch AI keywords. Using local keyword analysis."
        );
      } finally {
        setLoading(false);
      }
    };

    updateKeywordData();
  }, [text, filter, getKeywords, setKeywords]);

  return (
    <Box
      className={`${!isInIframe ? "py-5 defaultText" : "py-1 interDisplay"}`}
    >
      <div
        style={{
          width: "100%",
          padding: "1rem",
          maxWidth: "1600px",
          margin: "0 auto",
          minHeight: isMobile ? "500px" : "600px",
        }}
      >
        <div style={{ width: "100%", marginBottom: "1rem" }}>
          {!isInIframe && (
            <ToolsHeader
              title="Keyword Density Checker"
              description="Use this tool to analyze keyword density in your content and assess SEO optimization."
            />
          )}
        </div>
        {errorMessage && (
          <div
            style={{
              backgroundColor: "#f8d7da",
              color: "#721c24",
              padding: "1rem",
              borderRadius: "4px",
              marginBottom: "1rem",
              textAlign: "center",
            }}
          >
            {errorMessage}
          </div>
        )}
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "1rem",
            flexWrap: "wrap",
            alignItems: "stretch",
            minHeight: isMobile ? "500px" : "600px",
          }}
        >
          <div style={{ flex: "0 0 auto", width: isMobile ? "100%" : "500px" }}>
            <LeftSection
              text={text}
              onTextChange={setText}
              onUrlChange={handleUrlChange}
              onKeywordChange={handleKeywordChange}
              onPrimaryKeywordChange={setPrimaryKeyword}
              onSecondaryKeyword1Change={setSecondaryKeyword1}
              onSecondaryKeyword2Change={setSecondaryKeyword2}
              onSubmit={handleSubmit}
              onTableDataChange={handleTableDataChange}
              onActiveTabChange={handleActiveTabChange}
              primaryKeyword={primaryKeyword}
              secondaryKeyword1={secondaryKeyword1}
              secondaryKeyword2={secondaryKeyword2}
              url={url}
            />
          </div>
          <div style={{ flex: "1 1 auto", minWidth: "300px" }}>
            <RightSection
              keywordData={keywordData}
              filter={filter}
              onFilterChange={setFilter}
              primaryKeyword={primaryKeyword}
              secondaryKeyword1={secondaryKeyword1}
              secondaryKeyword2={secondaryKeyword2}
              primaryKeywordData={primaryKeywordData}
              secondaryKeyword1Data={secondaryKeyword1Data}
              secondaryKeyword2Data={secondaryKeyword2Data}
              keywordPlacementData={keywordPlacementData}
              hasFetched={hasFetched}
              loading={loading}
              activeTab={activeTab}
            />
          </div>
        </div>
        <HelpModal
          helpSteps={helpSteps}
          show={showVideoModal}
          close={closeVideoModal}
          videoUrl="https://www.youtube.com/embed/hNce9hFpBOY"
        />
      </div>
    </Box>
  );
};

export default KeywordDensityChecker;